import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteWMS2021_R01, disableWMS2021_R01, downloadExcel_WMS2021_R01, getPdfBlob_WMS2021_R01, getWMS2021_R01AllCount, getWMS2021_R01List, patchWMS2021_R01, postWMS2021_R01 } from "./Api_WMS2021_R01";
import { defaultWMS2021_R01Filter, WMS2021_R01Filter, useWMS2021_R01Store, defaultWMS2021_R01ProcessFilter, WMS2021_R01ProcessFilter } from "./Store_WMS2021_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultWMS2021_R01Dto, WMS2021_R01Dto } from "./Dto_WMS2021_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<WMS2021_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

const getWMS2021_R01GroupKey = (item: WMS2021_R01Dto) =>
	`${item.logicCenter ?? ''}|${item.warehouseCd ?? ''}|${item.itemCd ?? ''}`;

const groupWMS2021_R01List = (list: WMS2021_R01Dto[]): WMS2021_R01Dto[] => {
	const groupMap = new Map<string, WMS2021_R01Dto>();

	list.forEach((item) => {
		const groupKey = getWMS2021_R01GroupKey(item);
		const lotQty = Number(item.lotQty) || 0;
		const existing = groupMap.get(groupKey);

		if (!existing) {
			groupMap.set(groupKey, {
				...defaultWMS2021_R01Dto,
				...item,
				groupKey,
				lotNo: '',
				lotQty,
				movedLotQty: Number(item.movedLotQty) || 0,
				oldWarehouseCd: item.oldWarehouseCd || item.warehouseCd,
				oldLogicCenter: item.oldLogicCenter || item.logicCenter,
			});
			return;
		}

		existing.lotQty += lotQty;
	});

	return Array.from(groupMap.entries()).map(([groupKey, row]) => ({ ...row, groupKey }));
};

export const WMS2021_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		WMS2021_R01Filter, setWMS2021_R01Filter,
		WMS2021_R01PastFilter, setWMS2021_R01PastFilter,
		WMS2021_R01ProcessFilter, setWMS2021_R01ProcessFilter,
		WMS2021_R01List, setWMS2021_R01List, insertWMS2021_R01List, removeWMS2021_R01List,
		deletedWMS2021_R01List, addDeletedWMS2021_R01List, removeDeletedWMS2021_R01List,
		selectedWMS2021_R01List, setSelectedWMS2021_R01List, removeSelectedWMS2021_R01List,
		originalWMS2021_R01List, setOriginalWMS2021_R01List,
		WMS2021_R01DetailList, setWMS2021_R01DetailList, insertWMS2021_R01DetailList, removeWMS2021_R01DetailList,
		deletedWMS2021_R01DetailList, addDeletedWMS2021_R01DetailList, removeDeletedWMS2021_R01DetailList,
		selectedWMS2021_R01DetailList, setSelectedWMS2021_R01DetailList, removeSelectedWMS2021_R01DetailList,
		originalWMS2021_R01DetailList, setOriginalWMS2021_R01DetailList,
		allCount, setAllCount,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useWMS2021_R01Store();

	const { fetchOrgCdCode, wmsCdList, warehouseCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const { company, userInfo } = useUserStore();

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("WMS2021_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});
	
	
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_WMS2021_R01(
			'',
			getParams(WMS2021_R01Filter),
			WMS2021_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '생산계획 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [WMS2021_R01Filter, WMS2021_R01List])

	

	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_WMS2021_R01(
				'',
				getParams(WMS2021_R01Filter),
				WMS2021_R01List.length ?? 0,
				company?.compCd || ''
			);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
					// showWarning("QZ 출력 실패로 브라우저 미리보기로 대체합니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [WMS2021_R01Filter, WMS2021_R01List, company?.compCd, printPdfFromBlob, setLoadingSpinner, setMessage]);

	const getParams = (filter: WMS2021_R01Filter) => {
		const params: Partial<WMS2021_R01Filter> = {
			logicCenter: filter.logicCenter || undefined,
			warehouseCd: filter.warehouseCd || undefined,
			brandCd: filter.brandCd || undefined,
			itemCd: filter.itemCd || undefined,
		};
		return params;
	}
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultWMS2021_R01Row = useCallback(() => {

		return {
			...defaultWMS2021_R01Dto,
			inserted: true,
		}
	}, [defaultWMS2021_R01Dto, wmsCdList, warehouseCdList]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getWMS2021_R01AllCount(getParams(WMS2021_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [WMS2021_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);

		if (fetchType === 'search' || messageMode === 'show') {
			setScrollTop(null);
			setDetailScrollTop(null);
		}
	
		let initialListLength = WMS2021_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setWMS2021_R01List([]);
		}

		const responseData = await getWMS2021_R01List(
			'',
			getParams(WMS2021_R01Filter),
			fetchType === 'refetch' ? initialListLength : WMS2021_R01List.length ?? 0
		);
	
		const rawList = [...responseData.data.data] as WMS2021_R01Dto[];
		const newList = groupWMS2021_R01List(rawList);
		setWMS2021_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalWMS2021_R01List(newList);
		setSelectedWMS2021_R01List([]);
		removeDeletedWMS2021_R01List(deletedWMS2021_R01List);
	
		if (newList.length === 0) {
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setWMS2021_R01PastFilter(WMS2021_R01Filter);
		setWMS2021_R01ProcessFilter(defaultWMS2021_R01ProcessFilter);
		setLoadingSpinner(false);
	}, [
		WMS2021_R01Filter,  WMS2021_R01PastFilter, WMS2021_R01List, deletedWMS2021_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalWMS2021_R01List, setSelectedWMS2021_R01List, insertWMS2021_R01List,
		defaultWMS2021_R01Row, setScrollTop, setDetailScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedWMS2021_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedWMS2021_R01List(deletedWMS2021_R01List);
			removeSelectedWMS2021_R01List();
			setWMS2021_R01List([...WMS2021_R01List]);
		} else if (selectedWMS2021_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedWMS2021_R01List(selectedWMS2021_R01List);
			removeSelectedWMS2021_R01List(selectedWMS2021_R01List);
		}
	}, [selectedWMS2021_R01List, deletedWMS2021_R01List, addDeletedWMS2021_R01List, removeDeletedWMS2021_R01List, removeSelectedWMS2021_R01List, setWMS2021_R01List, WMS2021_R01List]);

	const validateInsertData = (data: WMS2021_R01Dto, processFilter: WMS2021_R01ProcessFilter ,index: number, valid: boolean) => {

		if(!validateString(data.logicCenter)) {
			if(valid) showWarning('물류센터를 선택해주세요.');
			return false;
		}
		if(!validateString(data.warehouseCd)) {
			if(valid) showWarning('창고를 선택해주세요.');
			return false;
		}

		if(processFilter.warehouseCd === data.oldWarehouseCd && processFilter.logicCenter === data.oldLogicCenter) {
			if(valid) showWarning('동일한 물류센터와 창고로는 작업할수 없습니다.');
			return false;
		}

		if(processFilter.processType === 'split' && data.movedLotQty <= 0) {
			if(valid) showWarning('분할작업시 이동량은 0 이상이어야 합니다.');
			return false;
		}
		
		return true;
	}
	
	
	const handleSaveClick = useCallback(async () => {
		setLoadingSpinner(true);
		// 이관 lot 처리
		const modifiedList = WMS2021_R01DetailList.filter(row => !deletedWMS2021_R01List.includes(row.lotNo));
		if(modifiedList.length === 0) {
			showWarning('이동분할작업에 필요한 정보가 없습니다.');
			setLoadingSpinner(false);
			return;
		}
		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = WMS2021_R01DetailList.findIndex(user => user.lotNo === value.lotNo);
			if(!validateString(WMS2021_R01ProcessFilter.warehouseCd)) { showWarning('작업대상창고를 선택해주세요.'); valid = false; return; }
			if(!validateString(WMS2021_R01ProcessFilter.logicCenter)) { showWarning('작업대상물류센터를 선택해주세요.'); valid = false; return; }
			if(!validateString(WMS2021_R01ProcessFilter.processType)) { showWarning('작업 유형을 선택해주세요.'); valid = false; return; }
			if(!validateInsertData(value, WMS2021_R01ProcessFilter, index, valid)) valid = false;
			seenMenuSet.add(value.lotNo);
		});
		if(!valid) { 
			setLoadingSpinner(false);
			return; 
		}
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;

		const [modifiedResponse] = await Promise.all([
			modifiedList.length > 0 ? patchWMS2021_R01(modifiedList, WMS2021_R01ProcessFilter.processType) : Promise.resolve(),
		]);

		if(modifiedResponse) {
			showSuccess('이동분할정보 작업이 완료되었습니다.');
			setMessage('이동분할정보 작업이 완료되었습니다.');
			setWMS2021_R01DetailList([]);
			setSelectedWMS2021_R01DetailList([]);
		}

		setTimeout(() => { setMessage(''); }, 5000);
		setLoadingSpinner(false);
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [
		WMS2021_R01ProcessFilter,
		deletedWMS2021_R01List,
		WMS2021_R01DetailList,  setWMS2021_R01DetailList,
		setSelectedWMS2021_R01DetailList,
		executeDataFetch, 
	]);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedWMS2021_R01List.length === 0 && selectedWMS2021_R01List.length === 0,
			save: WMS2021_R01List.length === 0,
			print: WMS2021_R01List.length === 0,
			excel: WMS2021_R01List.length === 0
		}));
	}, [selectedWMS2021_R01List, deletedWMS2021_R01List, WMS2021_R01List]);

	return{
		buttonDisabled,
		handleExcelClick,
		handlePrintClick,
		defaultWMS2021_R01Row,
		fetchData,
		initContainerData,
		handleDeleteClick,
		handleSaveClick,
	}
}
